import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LuSearch, LuUser, LuMail, LuLifeBuoy } from "react-icons/lu";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonList } from "@/components/ui/skeleton";

export function SearchPage() {
  const [q, setQ] = useState("");
  const r = useQuery({
    queryKey: ["console", "admin", "search", q],
    queryFn: () => api.adminSearch(q),
    enabled: q.length >= 2,
  });
  return (
    <>
      <PageHeader
        icon={LuSearch}
        title="Search"
        description="Paste any email, inbox address, user id, or ticket id."
      />
      <div className="p-8 space-y-6">
        <div className="relative max-w-xl">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-subtle)] pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="user@example.com / abcd@748392.best / id..."
            className="w-full pl-10 pr-3 py-2.5 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)]"
          />
        </div>
        {q.length < 2 && <p className="text-sm text-[var(--color-text-muted)]">Type at least 2 chars.</p>}
        {q.length >= 2 && r.isLoading && <SkeletonList rows={4} />}
        {r.data && (
          <>
            <ResultGroup label="Users" icon={LuUser} count={r.data.users.length}>
              {r.data.users.map((u) => (
                <Row key={u.id} primary={u.email} secondary={`${u.id} · ${u.subscription_tier}`} href={`/users#${u.id}`} />
              ))}
            </ResultGroup>
            <ResultGroup label="Inboxes" icon={LuMail} count={r.data.inboxes.length}>
              {r.data.inboxes.map((i) => (
                <Row key={i.id} primary={i.address} secondary={`expires ${new Date(i.expires_at).toLocaleString()}`} href={`/inboxes#${i.id}`} />
              ))}
            </ResultGroup>
            <ResultGroup label="Tickets" icon={LuLifeBuoy} count={r.data.tickets.length}>
              {r.data.tickets.map((t) => (
                <Row key={t.id} primary={t.subject} secondary={t.status} href={`/support#${t.id}`} />
              ))}
            </ResultGroup>
          </>
        )}
      </div>
    </>
  );
}

function ResultGroup({ label, icon: Icon, count, children }: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[var(--color-text-subtle)]" />
          <p className="text-sm font-semibold text-[var(--color-text)]">{label}</p>
        </div>
        <span className="text-[10px] text-[var(--color-text-subtle)]">{count}</span>
      </div>
      {count === 0 ? (
        <p className="px-5 py-4 text-xs text-[var(--color-text-subtle)]">No matches.</p>
      ) : (
        <div className="divide-y divide-[var(--color-border)]">{children}</div>
      )}
    </Card>
  );
}

function Row({ primary, secondary, href }: { primary: string; secondary: string; href: string }) {
  return (
    <a
      href={href}
      className="block px-5 py-3 hover:bg-[var(--color-elevated)]/30 transition-colors"
    >
      <p className="text-sm text-[var(--color-text)] truncate">{primary}</p>
      <p className="text-[11px] text-[var(--color-text-subtle)] font-mono truncate">{secondary}</p>
    </a>
  );
}
